import React from 'react';
import { Box, Typography } from '@mui/material';
import StackComposer from '../common/StackComposer';
import config from '../../config';
import { useAuth } from '../../AuthContext';
import { LANDING_FONT, LC, MAXW, PAD_X, r } from './landingTokens';

// ─── Hero ─────────────────────────────────────────────────────────────────────
// Green gradient + headline + the goal-input card. The card wraps the real
// StackComposer (chips, More goals, rotating placeholder); submitting runs the
// live funnel (onSubmit → sessionStorage + analytics → /create-stack).
export default function LandingHero({ healthGoals, setHealthGoals, onSubmit }) {
  // The medications hint goes with the composer's Medications control, which
  // only a logged-out visitor gets (STACK-565).
  // The line waits for auth to resolve, so a returning member never sees the
  // logged-out wording flash before it disappears (PR 390 review).
  const auth = useAuth();
  const signedIn = Boolean(auth?.currentUser);
  const showMicrocopy = Boolean(auth) && !auth.loading && !signedIn;
  const showMedicationsHint = Boolean(config.FEATURES.GUEST_MEDICATION_CHECK) && showMicrocopy;
  return (
    <Box
      component="header"
      id="top"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // svh keeps the hero exactly one screen tall on mobile despite the
        // browser address bar (100vh overflows/jumps as it shows/hides).
        minHeight: '100vh',
        '@supports (min-height: 100svh)': { minHeight: '100svh' },
        // The floating bottom nav overlays the last ~5.25rem of the viewport
        // on mobile; without this inset the flex-centered block sits ~40px
        // below the optical center of the VISIBLE band (viewport minus nav).
        pb: { xs: '5.25rem', md: 0 },
        background: [
          `radial-gradient(60% 50% at 12% 8%, rgba(159,217,190,.16), transparent 70%)`,
          `radial-gradient(45% 40% at 95% 100%, rgba(10,156,95,.28), transparent 70%)`,
          `radial-gradient(115% 120% at 88% -10%, ${LC.heroMid} 0%, ${LC.evergreen} 44%, ${LC.heroDark} 100%)`,
        ].join(', '),
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: MAXW,
          px: PAD_X,
          // symmetric padding; the fixed transparent nav overlays the top edge.
          py: 'clamp(6rem, 12vw, 8rem)',
          // Centered hero block (flex, not mx:auto — the global
          // `.MuiBox-root { margin: 0 !important }` rule defeats margin
          // centering on any Box).
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {/* No entrance animation on the hero — full-screen, immediate on load. */}
        <Typography
          component="h1"
          sx={{
            fontFamily: LANDING_FONT,
            // 2.125rem floor keeps the headline to three lines on phones (the
            // 2.5rem floor wrapped it to four, towering over the hero);
            // unchanged above ~610px where the vw term takes over.
            fontSize: 'clamp(2.125rem, 5.6vw, 4rem)',
            fontWeight: 800,
            letterSpacing: '-0.04em',
            lineHeight: 1.02,
            color: 'primary.contrastText',
          }}
        >
          Supplement stacks built on evidence,{' '}
          <Box component="span" sx={{ color: LC.mint }}>not hope</Box>.
        </Typography>

        {/* Lead line on the gradient (per the mock's hero__lead) — the card
            itself stays chrome-free, Perplexity-style. */}
        <Typography
          sx={{
            fontFamily: LANDING_FONT,
            fontSize: 'clamp(0.9375rem, 2vw, 1.125rem)',
            color: LC.heroLead,
            mt: r(22),
            lineHeight: 1.55,
          }}
        >
          Type your goal, or pick a common one.
        </Typography>

        {/* The composer IS the hero card — one container (bg/radius/shadow
            live inside StackComposer for surface="landing"); no wrapper card.
            48rem cap: chat-composer norm (ChatGPT 48rem, Perplexity ~40rem) —
            input width signals expected answer length, and a goal is a short
            phrase; uncapped at 1080px it read as a blank slab. 48rem is also
            the floor at which the six desktop goal chips + "More goals" +
            submit still fit one row. Centered with the rest of the hero. */}
        {/* textAlign resets to left inside the card — input text and chips
            read as a chat composer, not centered copy. */}
        <Box sx={{ mt: r(14), width: '100%', maxWidth: '48rem', textAlign: 'left' }}>
          <StackComposer
            healthGoals={healthGoals}
            setHealthGoals={setHealthGoals}
            onSubmit={onSubmit}
            surface="landing"
            showHeader={false}
          />
        </Box>

        {/* One line of microcopy below the card, on the gradient (Aravind,
            2026-10-09; replaces "2 free stacks. No sign-up required." and the
            medications hint). A signed-in visitor gets no line here: no
            sign-up to promise, and no medications to mention. */}
        {showMicrocopy && (
          <Typography
            data-testid="landing-microcopy"
            sx={{ fontFamily: LANDING_FONT, fontSize: r(13), color: 'rgba(255,255,255,0.72)', mt: r(12), textAlign: 'center' }}
          >
            {showMedicationsHint ? (
              <>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  Free · Check against your medications
                </Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Free · No sign-up · Add your medications to check for interactions.
                </Box>
              </>
            ) : 'Free · No sign-up'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
